import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, FolderOpen, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDropboxClientFolders, useInvalidateDropboxCache } from "@/hooks/useDropboxFolders";
import { useUpsertClientFacilityFolder, useClientFacilityFolders, Client } from "@/hooks/useClients";
import { matchClientDropboxFolder } from "@/lib/document-folder-matching";

interface DropboxFolderDialogFacility {
  id: string;
  code: string;
  dropbox_folder_prefix: string | null;
}

interface DropboxFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
  facility: DropboxFolderDialogFacility | null;
}

export function DropboxFolderDialog({ open, onOpenChange, client, facility }: DropboxFolderDialogProps) {
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [autoMatched, setAutoMatched] = useState(false);
  const { data: dropboxFolders, isLoading: foldersLoading } = useDropboxClientFolders(facility?.id);
  const { data: existingFolders } = useClientFacilityFolders(client?.id);
  const { invalidateClientFolders } = useInvalidateDropboxCache();
  const upsertFolder = useUpsertClientFacilityFolder();

  const savedFolder = useMemo(
    () => existingFolders?.find((f) => f.facility_id === facility?.id)?.dropbox_folder ?? null,
    [existingFolders, facility?.id]
  );

  // Find suggested folder using fuzzy matching
  const suggestedFolder = useMemo(() => {
    if (!client || !facility?.dropbox_folder_prefix || !dropboxFolders || dropboxFolders.length === 0) return null;
    return matchClientDropboxFolder(client, dropboxFolders, facility.dropbox_folder_prefix, savedFolder);
  }, [client, facility, dropboxFolders, savedFolder]);

  // Reset selection when dialog opens with a new client/facility, auto-select if match found
  useEffect(() => {
    if (open && client && facility) {
      if (savedFolder) {
        setSelectedFolder(savedFolder);
        setAutoMatched(false);
      } else if (suggestedFolder) {
        setSelectedFolder(suggestedFolder);
        setAutoMatched(true);
      } else {
        setSelectedFolder("");
        setAutoMatched(false);
      }
    }
  }, [open, client, facility, savedFolder, suggestedFolder]);

  const handleSave = () => {
    if (!client || !facility || !selectedFolder) return;

    upsertFolder.mutate(
      { clientId: client.id, facilityId: facility.id, dropboxFolder: selectedFolder },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Set {facility?.code} Dropbox Folder
          </DialogTitle>
          <DialogDescription>
            Select the {facility?.code} Dropbox folder for <strong>{client?.name}</strong>. This will be used as the default destination when routing {facility?.code} documents for this client.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Dropbox Folder</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => invalidateClientFolders()}
              disabled={foldersLoading}
            >
              <RefreshCw className={cn("h-3 w-3 mr-1", foldersLoading && "animate-spin")} />
              Refresh
            </Button>
          </div>

          <Command className="rounded-lg border">
            <CommandInput placeholder="Search folders..." />
            <CommandList className="max-h-60">
              {foldersLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading folders...</span>
                </div>
              ) : (
                <>
                  <CommandEmpty>No folder found.</CommandEmpty>
                  <CommandGroup>
                    {dropboxFolders?.map((folder) => (
                      <CommandItem
                        key={folder.path}
                        value={folder.name}
                        onSelect={() => setSelectedFolder(folder.name)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedFolder === folder.name ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {folder.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>

          {selectedFolder && (
            <div className="mt-2 flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{selectedFolder}</span>
              </p>
              {autoMatched && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Sparkles className="h-3 w-3" />
                  Auto-matched
                </Badge>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedFolder || upsertFolder.isPending}
          >
            {upsertFolder.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Mapping"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
